import { Controller } from "@hotwired/stimulus";

/**
 * GO4SXPENSE — the one-page entry of an expense (29/10/2026), which saves
 * itself: two seconds after the last change the page is sent as a draft
 * (JSON, without the files, which go with the buttons) and its state is
 * shown (« Enregistrement… », « Brouillon enregistré », « Échec »).
 *
 * The first save of a new page opens the draft: the form then updates it,
 * and the duplicate warning is reloaded after each save. A button pressed
 * while a save is under way waits for it, so the page never opens two drafts.
 *
 * <form data-controller="expense-draft" data-expense-draft-url-value="/…/expense_drafts"
 *       data-action="input->expense-draft#changed change->expense-draft#changed submit->expense-draft#submitted">
 *   <input data-expense-draft-target="total">      the total with tax, written by `expense`
 *   <strong data-expense-draft-target="totalLabel">
 *   <span data-expense-draft-target="status">
 *   <turbo-frame data-expense-draft-target="duplicates">
 */
export default class extends Controller {
  static targets = ["status", "total", "totalLabel", "duplicates"];
  static values = {
    url: String, persisted: Boolean, saving: String, saved: String, failed: String,
  };

  disconnect() {
    clearTimeout(this.timer);
  }

  changed(event) {
    this.showTotal();
    const target = event?.target;
    if (!target || target.type === "file" || target.form !== this.element) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(), 2000);
  }

  // Enter in a field never presses the first button of the bar.
  blockEnter(event) {
    if (event.key === "Enter" && event.target.tagName === "INPUT" && event.target.type !== "submit") {
      event.preventDefault();
    }
  }

  // A button of the bar sends the page: the pending save is not needed, and
  // one under way is waited for (it may be opening the draft).
  submitted(event) {
    clearTimeout(this.timer);
    if (!this.inFlight) return;

    event.preventDefault();
    const submitter = event.submitter;
    this.inFlight.finally(() => this.element.requestSubmit(submitter));
  }

  async save() {
    if (this.inFlight) {
      this.again = true;
      return;
    }
    this.inFlight = this.send();
    try {
      await this.inFlight;
    } finally {
      this.inFlight = null;
    }
    if (this.again) {
      this.again = false;
      this.save();
    }
  }

  async send() {
    this.show(this.savingValue);
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrf },
        body: this.body(),
        credentials: "same-origin",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.saved) {
        this.show(this.failedValue);
        return;
      }
      this.opened(data);
      this.show(`${this.savedValue} ${data.saved_at || ""}`.trim());
      this.reloadDuplicates(data.duplicates_url);
    } catch (_) {
      this.show(this.failedValue);
    }
  }

  // What the page holds, without the files and the boxes to remove them:
  // those go with the buttons only.
  body() {
    const body = new FormData(this.element);
    for (const [key, value] of [...body.entries()]) {
      if (value instanceof File || key === "remove_receipt_ids[]" || key === "commit_action") body.delete(key);
    }
    if (this.persistedValue) body.set("_method", "patch");
    return body;
  }

  // The first save opened the draft: the form now updates it.
  opened(data) {
    if (this.persistedValue || !data.url) return;

    this.persistedValue = true;
    this.urlValue = data.url;
    this.element.action = data.url;
    const method = document.createElement("input");
    method.type = "hidden";
    method.name = "_method";
    method.value = "patch";
    this.element.prepend(method);
    if (data.edit_url) {
      this.element.dataset.formRefreshUrlValue = data.edit_url;
      // The address follows: reloading the page, or coming back to it, opens
      // the draft instead of an empty entry.
      window.history.replaceState(window.history.state, "", data.edit_url);
    }
  }

  reloadDuplicates(url) {
    if (!this.hasDuplicatesTarget || !url) return;

    const frame = this.duplicatesTarget;
    if (frame.getAttribute("src") === url && typeof frame.reload === "function") {
      frame.reload();
    } else {
      frame.src = url;
    }
  }

  showTotal() {
    if (!this.hasTotalTarget || !this.hasTotalLabelTarget) return;

    const total = Number.parseFloat(this.totalTarget.value) || 0;
    this.totalLabelTarget.textContent = `${new Intl.NumberFormat("fr-FR").format(total)} F CFA`;
  }

  show(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
